import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, API_URL, formatApiError, getActiveClinicId } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { toast } from "sonner";
import {
  ArrowLeft,
  FloppyDisk,
  DownloadSimple,
  EnvelopeSimple,
  Plus,
  Trash,
  CheckCircle,
  Warning,
  Circle,
} from "@phosphor-icons/react";
import LabTable from "@/components/LabTable";
import DiagnosisSelect from "@/components/DiagnosisSelect";
import BillingPanel from "@/components/BillingPanel";
import ConsultationAttachments from "@/components/ConsultationAttachments";
import RichTextField from "@/components/RichTextField";
import DatePickerZoom from "@/components/DatePickerZoom";
import PrintableConsultation from "@/components/PrintableConsultation";
import ExtendedConsultationTabs from "@/components/ExtendedConsultationTabs";
import { Printer } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDate } from "@/lib/dateFmt";
import { createPortal } from "react-dom";

const AUTOSAVE_DELAY = 900;

export default function ConsultationPage() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [patient, setPatient] = useState(null);
  const [vets, setVets] = useState([]);
  const [visitReasons, setVisitReasons] = useState([]);
  const [reasonQuery, setReasonQuery] = useState("");
  const [refRanges, setRefRanges] = useState({ items: [] });
  const [saving, setSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [emailOpen, setEmailOpen] = useState(false);
  const [allDiagnoses, setAllDiagnoses] = useState([]);

  const saveTimer = useRef(null);
  const isFirstLoad = useRef(true);
  const cRef = useRef(null);
  const dirtyRef = useRef(false);

  // Always keep a ref to the latest consultation state so flush handlers
  // (blur / route change / tab hidden) can persist the freshest data.
  useEffect(() => { cRef.current = c; }, [c]);

  function buildSaveBody(src) {
    return {
      visit_reasons: src.visit_reasons || [],
      private_notes: src.private_notes || "",
      anamnesis: src.anamnesis || "",
      temperature: src.temperature || "",
      weight: src.weight || "",
      clinical_examination: src.clinical_examination || "",
      biochemistry: src.biochemistry || [],
      cbc: src.cbc || [],
      diagnosis: src.diagnosis || "",
      diagnosis_ids: src.diagnosis_ids || [],
      billing_items: src.billing_items || [],
      treatment: src.treatment || "",
      recommendations: src.recommendations || "",
      observations: src.observations || "",
      attending_vet_id: src.attending_vet_id || null,
      extended_sections: src.extended_sections || null,
    };
  }

  // Best-effort synchronous save when the tab is being hidden/closed. Uses
  // fetch keepalive so the request survives the page unload — this is what
  // prevents data loss on iOS Safari when the user backgrounds the app.
  function beaconSave() {
    const src = cRef.current;
    if (!src || !dirtyRef.current) return;
    try {
      const headers = { "Content-Type": "application/json" };
      const clinicId = getActiveClinicId();
      if (clinicId) headers["X-Pawfile-Clinic-Id"] = clinicId;
      fetch(`${API_URL}/consultations/${id}`, {
        method: "PATCH",
        credentials: "include",
        keepalive: true,
        headers,
        body: JSON.stringify(buildSaveBody(src)),
      }).catch(() => {});
      dirtyRef.current = false;
    } catch (_) { /* ignore */ }
  }

  async function load() {
    try {
      const { data } = await api.get(`/consultations/${id}`);
      setC(data);
      const [pRes, uRes, rRes] = await Promise.all([
        api.get(`/patients/${data.patient_id}`),
        api.get("/users"),
        Promise.resolve(null),
      ]);
      setPatient(pRes.data);
      setVets(uRes.data.filter((u) => u.role !== "assistant"));
      api.get("/diagnoses").then((d) => setAllDiagnoses(d.data || [])).catch(() => {});
      const rangeRes = await api.get(`/reference-ranges/${pRes.data.species}`);
      setRefRanges(rangeRes.data || { items: [] });
      // Hydrate lab rows with ref-range metadata, but do NOT create rows out of thin air —
      // new consultations start with empty tables; the vet adds a row when they need one.
      const hydrate = (rows, category) => {
        const items = (rangeRes.data?.items || []).filter((x) => x.category === category);
        if (!rows || rows.length === 0) return [];
        // ensure ref_min/max present on any rows the vet already added
        return rows.map((r) => {
          if (r.ref_min == null && r.ref_max == null) {
            const found = items.find((it) => it.parameter?.toLowerCase() === (r.parameter || "").toLowerCase());
            if (found) return { ...r, ref_min: found.min_value, ref_max: found.max_value, unit: r.unit || found.unit };
          }
          return r;
        });
      };
      setC({
        ...data,
        biochemistry: hydrate(data.biochemistry, "biochemistry"),
        cbc: hydrate(data.cbc, "cbc"),
      });
      const vrRes = await api.get("/visit-reasons");
      setVisitReasons(vrRes.data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }
  useEffect(() => {
    load();
    isFirstLoad.current = true;
    // eslint-disable-next-line
  }, [id]);

  useEffect(() => {
    if (!c) return;
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(save, AUTOSAVE_DELAY);
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [c && c.visit_reasons, c && c.private_notes, c && c.anamnesis, c && c.temperature, c && c.weight,
    c && c.clinical_examination, c && c.biochemistry, c && c.cbc, c && c.diagnosis,
    c && c.diagnosis_ids, c && c.billing_items,
    c && c.treatment, c && c.recommendations, c && c.observations, c && c.attending_vet_id,
    c && c.extended_sections]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Persist pending edits when the tab is backgrounded/closed or the user
  // navigates away from the consultation (route change unmounts this page).
  useEffect(() => {
    const onHide = () => {
      if (document.visibilityState === "hidden") beaconSave();
    };
    window.addEventListener("pagehide", beaconSave);
    window.addEventListener("beforeunload", beaconSave);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("pagehide", beaconSave);
      window.removeEventListener("beforeunload", beaconSave);
      document.removeEventListener("visibilitychange", onHide);
      flushSave();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);


  async function save(newStatus, extra) {
    const src = cRef.current;
    if (!src) return;
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    setSaving(true);
    try {
      const body = { ...buildSaveBody(src), ...(extra || {}) };
      if (newStatus) body.status = newStatus;
      const { data } = await api.patch(`/consultations/${id}`, body);
      dirtyRef.current = false;
      setC((prev) => ({ ...prev, status: data.status, updated_at: data.updated_at, created_at: data.created_at || prev.created_at }));
      setLastSaved(new Date());
      if (newStatus === "final") toast.success("Consultation finalized");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  // Immediately persist any pending change (called on field blur so a vet
  // never loses an edit by tapping away before the autosave debounce fires).
  function flushSave() {
    if (!dirtyRef.current) return;
    if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
    save();
  }

  function set(k, v) {
    dirtyRef.current = true;
    setC((prev) => ({ ...prev, [k]: v }));
  }

  function toggleReason(label) {
    const current = c.visit_reasons || [];
    if (current.includes(label)) {
      set("visit_reasons", current.filter((x) => x !== label));
    } else {
      set("visit_reasons", [...current, label]);
    }
  }

  async function downloadPdf() {
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    // iOS Safari blocks window.open() that runs *after* an await (it loses the
    // user-gesture context). So on iOS we open the tab synchronously now and
    // point it at the blob once the PDF has been fetched.
    let iosWin = null;
    if (isIOS) iosWin = window.open("", "_blank");
    try {
      const res = await api.get(`/consultations/${id}/pdf`, { params: { lang }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const filename = `consultatie-${(patient?.name || "pacient").replace(/\s+/g, "-")}-${id.slice(0, 8)}.pdf`;
      if (isIOS) {
        if (iosWin) {
          iosWin.location = url;
        } else {
          // Popup blocked — navigate current tab as a last resort.
          window.location.href = url;
        }
      } else {
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (iosWin) iosWin.close();
      toast.error(formatApiError(e));
    }
  }

  async function printConsult() {
    // Print the EXACT same document as Download: fetch the server PDF and open
    // it inline in a new tab (blob URLs ignore Content-Disposition), where the
    // user prints from the browser's native PDF viewer. iOS Safari blocks
    // window.open() after an await, so pre-open the tab synchronously.
    const ua = window.navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const win = window.open("", "_blank");
    try {
      const res = await api.get(`/consultations/${id}/pdf`, { params: { lang }, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      if (win) {
        win.location = url;
        if (!isIOS) {
          // Desktop: trigger the print dialog once the PDF viewer has loaded.
          win.addEventListener("load", () => { try { win.print(); } catch (_) {} });
        }
      } else {
        window.location.href = url;
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (win) win.close();
      toast.error(formatApiError(e));
    }
  }

  async function removeConsult() {
    if (!window.confirm("Delete this consultation?")) return;
    try {
      await api.delete(`/consultations/${id}`);
      navigate(`/patients/${c.patient_id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  async function createInvoiceFromConsult() {
    if (!c || !c.billing_items || c.billing_items.length === 0) return;
    try {
      const { data } = await api.post("/invoices", {
        patient_id: c.patient_id,
        consultation_id: c.id,
        currency: lang === "ro" ? "RON" : "USD",
        items: [],
      });
      toast.success(t("invoiceCreated"));
      navigate(`/invoices`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  if (!c || !patient) return <div className="text-sm text-pf-text-secondary">Loading…</div>;

  const savingLabel = saving
    ? t("saving")
    : lastSaved
      ? `${t("autosave")} · ${lastSaved.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`
      : t("autosave");

  async function updateDate(iso) {
    if (!iso) return;
    // Preserve the original time portion (or default to 09:00) so lists stay sensibly ordered.
    const oldDate = c.created_at ? new Date(c.created_at) : new Date();
    const [y, m, d] = iso.split("-").map(Number);
    const next = new Date(oldDate);
    next.setFullYear(y);
    next.setMonth(m - 1);
    next.setDate(d);
    setC((prev) => ({ ...prev, created_at: next.toISOString() }));
    await save(null, { created_at: next.toISOString() });
    toast.success(t("saved"));
  }

  const consultDateIso = c.created_at ? c.created_at.slice(0, 10) : "";

  const printDiagnoses = (c.diagnosis_ids || [])
    .map((did) => allDiagnoses.find((d) => d.id === did))
    .filter(Boolean);

  return (
    <div className="animate-fade-in-up">
      <Link
        to={`/patients/${patient.id}`}
        className="inline-flex items-center gap-1 text-sm text-pf-text-secondary hover:text-pf-text mb-4"
      >
        <ArrowLeft size={14} /> {patient.name}
      </Link>

      {/* Sticky action bar */}
      <div className="pf-card p-4 mb-5 sticky top-16 z-30 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-xs text-pf-text-secondary flex items-center gap-1.5">
            {saving ? (
              <Circle size={10} className="animate-pulse text-pf-warning" weight="fill" />
            ) : (
              <CheckCircle size={12} weight="fill" className="text-pf-success" />
            )}
            {savingLabel}
          </div>
          <h1 className="text-xl sm:text-2xl font-display font-bold text-pf-text flex items-center gap-2 flex-wrap">
            {patient.name}
            <span className="text-pf-text-secondary text-base font-normal">—</span>
            <div className="w-40" data-testid="consult-date-picker">
              <DatePickerZoom
                testId="consult-date"
                value={consultDateIso}
                onChange={updateDate}
              />
            </div>
          </h1>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button data-testid="download-pdf-btn" onClick={downloadPdf} className="pf-btn-ghost">
            <DownloadSimple size={14} /> {t("downloadPdf")}
          </button>
          <button data-testid="print-consult-btn" onClick={printConsult} className="pf-btn-ghost">
            <Printer size={14} /> {t("printSheet")}
          </button>
          <button data-testid="email-owner-btn" onClick={() => setEmailOpen(true)} className="pf-btn-ghost">
            <EnvelopeSimple size={14} /> {t("emailOwner")}
          </button>
          {c.status !== "final" ? (
            <button data-testid="finalize-btn" onClick={() => save("final")} className="pf-btn-primary">
              <FloppyDisk size={14} /> {t("saveFinal")}
            </button>
          ) : (
            <span className="pf-chip-primary">final</span>
          )}
          <button onClick={removeConsult} className="pf-btn-ghost !text-pf-danger !border-pf-danger/30 hover:!bg-pf-danger-bg">
            <Trash size={14} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-5">
        <div className="lg:col-span-8 space-y-5">
          {/* Visit reasons */}
          <Section title={t("visitReasons")}>
            <input
              data-testid="visit-reasons-search"
              className="pf-input mb-3"
              placeholder={t("searchPlaceholder")}
              value={reasonQuery}
              onChange={(e) => setReasonQuery(e.target.value)}
            />
            <div className="flex flex-wrap gap-2">
              {visitReasons.length === 0 && (
                <div className="text-sm text-pf-text-secondary">No visit reasons configured yet.</div>
              )}
              {visitReasons
                .filter((r) => {
                  const q = reasonQuery.trim().toLowerCase();
                  if (!q) return true;
                  return (r.label || "").toLowerCase().includes(q);
                })
                .map((r) => {
                const active = (c.visit_reasons || []).includes(r.label);
                return (
                  <button
                    key={r.id}
                    onClick={() => toggleReason(r.label)}
                    data-testid={`visit-reason-${r.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${
                      active
                        ? "bg-pf-primary text-white border-pf-primary shadow-card"
                        : "bg-white border-pf-border text-pf-text hover:bg-pf-primary-light hover:border-pf-primary/40"
                    }`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>
          </Section>

          <Section title={t("privateNotes")} icon={<Warning size={14} className="text-pf-warning" />}>
            <textarea
              data-testid="private-notes-input"
              rows={2}
              className="pf-input min-h-[70px] bg-pf-warning-bg/40 border-pf-warning/30"
              placeholder="Staff only — not included in reports or emails."
              value={c.private_notes || ""}
              onChange={(e) => set("private_notes", e.target.value)}
              onBlur={flushSave}
            />
          </Section>

          <Section title={t("anamnesis")}>
            <RichTextField
              testId="anamnesis"
              value={c.anamnesis || ""}
              onChange={(v) => set("anamnesis", v)}
              onBlur={flushSave}
            />
          </Section>

          <Section title={t("generalExam")}>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="pf-label mb-1 block">{t("temperature")} (°C)</label>
                <div className="relative">
                  <input
                    data-testid="temp-input"
                    className="pf-input pr-10"
                    inputMode="decimal"
                    value={c.temperature || ""}
                    onChange={(e) => set("temperature", e.target.value.replace(",", "."))}
                    onBlur={flushSave}
                    placeholder="38.5"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-pf-text-secondary pointer-events-none">°C</span>
                </div>
              </div>
              <div>
                <label className="pf-label mb-1 block">{t("weight")} (kg)</label>
                <div className="relative">
                  <input
                    data-testid="weight-input"
                    className="pf-input pr-10"
                    inputMode="decimal"
                    value={c.weight || ""}
                    onChange={(e) => set("weight", e.target.value.replace(",", "."))}
                    onBlur={flushSave}
                    placeholder="12.4"
                  />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-pf-text-secondary pointer-events-none">kg</span>
                </div>
              </div>
              <div className="col-span-2">
                <label className="pf-label mb-1 block">{t("clinicalExam")}</label>
                <RichTextField
                  testId="clinical-exam"
                  value={c.clinical_examination || ""}
                  onChange={(v) => set("clinical_examination", v)}
                  onBlur={flushSave}
                  snippetKind="clinical-examinations"
                />
              </div>
            </div>
          </Section>

          <Section title={`${t("examClinicTab")} & ${t("paraclinicalTab")}`}>
            <ExtendedConsultationTabs
              value={c.extended_sections || {}}
              onChange={(v) => set("extended_sections", v)}
              refItems={refRanges.items || []}
            />
          </Section>

          <Section title={t("diagnosis")}>
            <div className="mb-3">
              <div className="pf-label mb-1">{t("selectedDiagnoses")}</div>
              <DiagnosisSelect
                selectedIds={c.diagnosis_ids || []}
                onChange={(ids) => set("diagnosis_ids", ids)}
              />
            </div>
            <div>
              <div className="pf-label mb-1">{t("diagnosisNotes")}</div>
              <textarea
                data-testid="diagnosis-input"
                rows={3}
                className="pf-input min-h-[90px]"
                value={c.diagnosis || ""}
                onChange={(e) => set("diagnosis", e.target.value)}
                onBlur={flushSave}
              />
            </div>
          </Section>

          <Section title={t("treatment")}>
            <RichTextField
              testId="treatment"
              value={c.treatment || ""}
              onChange={(v) => set("treatment", v)}
              onBlur={flushSave}
            />
          </Section>

          <Section title={t("recommendations")}>
            <RichTextField
              testId="recommendations"
              value={c.recommendations || ""}
              onChange={(v) => set("recommendations", v)}
              onBlur={flushSave}
              snippetKind="recommendations"
            />
          </Section>

          <Section title={t("observations")}>
            <p className="text-xs text-pf-text-secondary mb-2">{t("observationsHint")}</p>
            <textarea
              data-testid="observations-input"
              rows={3}
              className="pf-input min-h-[90px]"
              value={c.observations || ""}
              onChange={(e) => set("observations", e.target.value)}
              onBlur={flushSave}
            />
          </Section>

          <Section title={t("proceduresAndProducts")}>
            <BillingPanel
              items={c.billing_items || []}
              onChange={(rows) => set("billing_items", rows)}
              currency={lang === "ro" ? "RON" : "USD"}
            />
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                onClick={createInvoiceFromConsult}
                className="pf-btn-ghost"
                data-testid="create-invoice-from-consult"
                disabled={!c.billing_items || c.billing_items.length === 0}
              >
                {t("createInvoiceFromConsult")}
              </button>
            </div>
          </Section>

          <Section title={t("consultAttachments")}>
            <ConsultationAttachments
              consultationId={c.id}
              attachments={c.attachments || []}
              onChange={(rows) => set("attachments", rows)}
            />
          </Section>
        </div>

        {/* Right sidebar */}
        <div className="lg:col-span-4 space-y-4">
          <Section title={t("attendingVet")}>
            <select
              data-testid="attending-vet-select"
              className="pf-input"
              value={c.attending_vet_id || ""}
              onChange={(e) => set("attending_vet_id", e.target.value)}
            >
              <option value="">—</option>
              {vets.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name} · {v.role}
                </option>
              ))}
            </select>
          </Section>

          <Section title={t("owner")}>
            <div className="text-sm space-y-1">
              <div className="font-semibold text-pf-text">{patient.owner?.full_name}</div>
              <div className="text-pf-text-secondary">{patient.owner?.phone}</div>
              <div className="text-pf-text-secondary">{patient.owner?.email}</div>
              <div className="text-pf-text-secondary text-xs">{patient.owner?.address}</div>
            </div>
          </Section>
        </div>
      </div>

      <EmailDialog
        open={emailOpen}
        onOpenChange={setEmailOpen}
        defaultEmail={patient.owner?.email || ""}
        consultationId={id}
        patientName={patient.name}
      />

      {createPortal(
        <PrintableConsultation
          consultation={c}
          patient={patient}
          diagnoses={printDiagnoses}
          lang={lang}
          t={t}
          tField={t}
          tSection={t}
          tGroup={t}
        />,
        document.body
      )}
    </div>
  );
}

function Section({ title, icon, children }) {
  return (
    <div className="pf-card p-5">
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <div className="pf-section-title">{title}</div>
      </div>
      {children}
    </div>
  );
}

function EmailDialog({ open, onOpenChange, defaultEmail, consultationId, patientName }) {
  const { t, lang } = useLang();
  const [email, setEmail] = useState(defaultEmail);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setEmail(defaultEmail);
  }, [defaultEmail]);

  async function send() {
    if (!email) {
      toast.error("Recipient email required");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/consultations/${consultationId}/email`, {
        recipient_email: email,
        message,
      }, { params: { lang } });
      toast.success(`Report emailed to ${email}`);
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-display">{t("emailOwner")} — {patientName}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="pf-label mb-1 block">{t("email")}</label>
            <input data-testid="email-recipient-input" className="pf-input" value={email} onChange={(e) => setEmail(e.target.value)} type="email" />
          </div>
          <div>
            <label className="pf-label mb-1 block">Message (optional)</label>
            <textarea rows={3} className="pf-input min-h-[100px]" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Attach a personal note…" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={() => onOpenChange(false)} className="pf-btn-ghost">{t("cancel")}</button>
            <button data-testid="send-email-btn" onClick={send} disabled={busy} className="pf-btn-primary">
              <EnvelopeSimple size={14} /> {busy ? "Sending…" : "Send"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
